import {
  BlotterTable,
  Box,
  type CareOrder,
  CareOrderStatus,
  type CareOrderStatusEnum,
  type Column,
  type ColumnDef,
  formattedUTCDay,
  logger,
  type MinimalSubscriptionResponse,
  POSITIVE_CELL_CLASSNAME,
  type SizeColumnParams,
  useBehaviorSubject,
  useBlotterTable,
  type ValueOf,
} from '@talos/kyoko';
import type { CellClassRules, GridOptions, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { compact, get } from 'lodash-es';
import { useMemo } from 'react';
import type { CareOrderParser, ImportedCareOrder } from '../types';
import { SIMILAR_CARE_ORDERS_COL_WIDTHS } from './SimilarCareOrdersBlotter';
import { getResultingCareOrder, type ResultingCareOrder } from './types';

interface ResultingCareOrderBlotterProps {
  importedCareOrder: ImportedCareOrder;
  selectedCareOrder: CareOrder;
  parser: CareOrderParser;
}

export const ResultingCareOrderBlotter = ({
  importedCareOrder,
  selectedCareOrder,
  parser,
}: ResultingCareOrderBlotterProps) => {
  const { observable: resultingCareOrderDataObs } = useBehaviorSubject(() => {
    return {
      initial: true,
      data: [getResultingCareOrder(importedCareOrder, selectedCareOrder)],
    } satisfies MinimalSubscriptionResponse<ResultingCareOrder>;
  }, [selectedCareOrder, importedCareOrder]);

  const resultingCareOrderBlotterGridOptions: GridOptions<ResultingCareOrder> = useMemo(
    () => ({
      defaultColDef: {
        suppressHeaderMenuButton: true, // dont allow modifying columns in this dialog
      },
      rowSelection: {
        checkboxes: true,
        mode: 'singleRow',
        enableClickSelection: false,
        hideDisabledCheckboxes: true,
        isRowSelectable: () => false, // we dont actually allow row selection, just need the width to line up the three blotters
      },
    }),
    []
  );

  const resultingCareOrderColumns = useResultingCareOrderColumns(selectedCareOrder, parser);
  const blotterTable = useBlotterTable({
    dataObservable: resultingCareOrderDataObs,
    errorObservable: undefined,
    columns: resultingCareOrderColumns,
    rowID: 'OrderID' satisfies keyof ResultingCareOrder,
    gridOptions: resultingCareOrderBlotterGridOptions,
  });

  return (
    <Box h="80px">
      {/* 80 is enough space for header, 1 row + scroll bar */}
      <BlotterTable {...blotterTable} background="backgroundModal" />
    </Box>
  );
};

const useResultingCareOrderColumns = (selectedCareOrder: CareOrder, parser: CareOrderParser) => {
  const cellClassRules: CellClassRules<ResultingCareOrder, unknown> = useMemo(() => {
    return {
      [POSITIVE_CELL_CLASSNAME]: params => {
        const field = params.colDef.field;
        if (!field) {
          logger.warn(
            `Unable to compute positive cell class name for resulting care order, colID: ${params.colDef.colId}`
          );
          return false;
        }

        const currentValue = get(selectedCareOrder, field);
        const newValue = get(params.data, field);
        const isAddition = newValue != null && currentValue == null;
        return isAddition;
      },
    };
  }, [selectedCareOrder]);

  const columns: Column[] = useMemo(() => {
    return compact([
      {
        type: 'custom',
        id: 'spacingcolumn', // does nothing, just spaces to align columns between the two blotters
        width: SIMILAR_CARE_ORDERS_COL_WIDTHS.warning,
        suppressColumnsToolPanel: true,
        suppressHeaderMenuButton: true,
      },
      parser === 'aladdin'
        ? {
            type: 'security',
            field: 'Symbol',
            title: 'Symbol',
            width: SIMILAR_CARE_ORDERS_COL_WIDTHS.symbol,
            cellClassRules,
          }
        : {
            type: 'text',
            field: 'Annotations.BasketOrder.BasketSymbol',
            title: 'Fund',
            width: SIMILAR_CARE_ORDERS_COL_WIDTHS.fund,
            cellClassRules,
          },
      {
        type: 'side',
        field: 'Side',
        title: 'Side',
        cellClassRules,
        width: SIMILAR_CARE_ORDERS_COL_WIDTHS.side,
      },
      parser === 'aladdin'
        ? {
            type: 'size',
            field: 'OrderQty',
            title: 'Order Qty',
            params: {
              currencyField: 'Currency',
            } satisfies SizeColumnParams<ResultingCareOrder>,
            cellClassRules,
            width: SIMILAR_CARE_ORDERS_COL_WIDTHS.orderQty,
          }
        : {
            type: 'number',
            field: 'units',
            title: 'Units',
            cellClassRules,
            width: SIMILAR_CARE_ORDERS_COL_WIDTHS.units,
          },
      {
        type: 'custom',
        id: 'status',
        width: SIMILAR_CARE_ORDERS_COL_WIDTHS.status,
        params: {
          valueGetter: (params: ValueGetterParams<ResultingCareOrder>) => {
            return params.data?.status;
          },
          cellRenderer: ({ value }: ICellRendererParams<unknown, ValueOf<typeof CareOrderStatusEnum>>) => {
            if (value) {
              return <CareOrderStatus status={value} />;
            }
            return '';
          },
        },
      },
      {
        type: 'date',
        field: 'SubmitTime',
        title: 'Submit Time',
        width: SIMILAR_CARE_ORDERS_COL_WIDTHS.submitTime,
        cellClassRules,
      },
      {
        type: 'text',
        field: 'Group',
        title: 'Group',
        width: SIMILAR_CARE_ORDERS_COL_WIDTHS.group,
        cellClassRules,
      },
      {
        type: 'text',
        field: 'Annotations.ETP.InitiatingFrom',
        title: 'Initiating From',
        width: SIMILAR_CARE_ORDERS_COL_WIDTHS.initiatingFrom,
        cellClassRules,
      },
      {
        type: 'text',
        field: 'Counterparty',
        title: 'Counterparty',
        width: SIMILAR_CARE_ORDERS_COL_WIDTHS.counterparty,
        cellClassRules,
      },
      parser === 'unqork' && {
        type: 'date',
        field: 'SettleDate',
        title: 'Settle Date',
        params: {
          customFormatter: formattedUTCDay,
        },
        width: SIMILAR_CARE_ORDERS_COL_WIDTHS.settleDate,
        cellClassRules,
      },
    ] satisfies (ColumnDef<ResultingCareOrder> | false)[]);
  }, [cellClassRules, parser]);

  return columns;
};
